import {useMemo, useState} from "react";
import {jwtDecode} from "jwt-decode";
import sendCoins from "../../api/sendCoins";
import useGetAllUsers from "../../hooks/useGetAllUsers";
import Spinner from "../spinner/Spinner";
import Message from "../message/Message";
import AdminLinksStyles from "./AdminCoinsContainer.module.css";

type TokenPayload = {
  email: string;
};

type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

const AdminCoinsContainer = () => {
  const {getAllUsersData, isLoading} = useGetAllUsers();
  const [message, setMessage] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [sendingCoinsLoader, setSendingCoinsLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [coins, setcoins] = useState<string>("");
  const currentUserEmail = useMemo(() => {
    const token = localStorage.getItem("accessTokenGC");
    if (!token) return "";

    try {
      const decoded = jwtDecode<TokenPayload>(token);
      return decoded.email;
    } catch {
      return "";
    }
  }, []);

  const onSelectUser = (user: User) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      if (exists) return prev;
      return [...prev, user];
    });
  };

  const onRemoveUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const filteredUsers = useMemo(() => {
    if (!getAllUsersData?.users) return [];

    const search = searchInput.toLowerCase();

    return getAllUsersData.users
      .filter((u: User) => u.email !== currentUserEmail)
      .filter((u: User) => !selectedUsers.some((s) => s.id === u.id))
      .filter(
        (u: User) =>
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(search) ||
          u.email.toLowerCase().includes(search),
      )
      .sort((a: User, b: User) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`,
        ),
      );
  }, [getAllUsersData, searchInput, selectedUsers, currentUserEmail]);

  const onSubmitHandlerGivecoins = async () => {
    try {
      setSendingCoinsLoader(true);

      const payload = {
        selectedIds: selectedUsers.map((u) => Number(u.id)),
        coins: Number(coins),
      };
      await sendCoins(payload);
      setMessage(true);
      setTimeout(() => {
        setMessage(false);
      }, 10000);
      setSelectedUsers([]);
      setSearchInput("");
      setcoins("");
      setIsModalOpen(false);
    } catch (error: any) {
      alert(error.message);
    } finally {
      setSendingCoinsLoader(false);
    }
  };
  return (
    <>
      {/* MAIN CONTAINER */}
      <div className={AdminLinksStyles.coinsContainerBlk}>
        {isLoading ? (
          <Spinner variant="medium-spinner" alignSpin="center-spinner" />
        ) : (
          <input
            className={AdminLinksStyles.searchInput}
            placeholder="Search users..."
            onFocus={() => setIsModalOpen(true)}
            readOnly
          />
        )}

        {/* coins INPUT */}
        <div className={AdminLinksStyles.coinsContainer}>
          <label className={AdminLinksStyles.coinsLabel}>Coins to assign</label>

          <input
            type="text"
            inputMode="numeric"
            className={AdminLinksStyles.coinsInput}
            value={coins}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d{0,6}$/.test(val)) {
                setcoins(val);
              }
            }}
          />
        </div>

        {/* SELECTED USERS */}
        <div className={AdminLinksStyles.selectedUsersWrapper}>
          {selectedUsers.length === 0 ? (
            <p className={AdminLinksStyles.emptyText}>No selected users</p>
          ) : (
            selectedUsers.map((user) => (
              <div key={user.id} className={AdminLinksStyles.selectedUserCard}>
                <div>
                  <div className={AdminLinksStyles.userName}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div className={AdminLinksStyles.userEmail}>{user.email}</div>

                  <div className={AdminLinksStyles.coinsTag}>
                    +{coins} coins
                  </div>
                </div>

                <button
                  className={AdminLinksStyles.removeButton}
                  onClick={() => onRemoveUser(user.id)}>
                  <span className={`fa-solid fa-xmark`}></span>
                </button>
              </div>
            ))
          )}
        </div>
        <button
          className={AdminLinksStyles.submitButton}
          disabled={
            selectedUsers.length === 0 ||
            !coins ||
            Number(coins) <= 0 ||
            sendingCoinsLoader
          }
          onClick={onSubmitHandlerGivecoins}>
          {sendingCoinsLoader ? (
            <Spinner variant="small-spinner" alignSpin="center-spinner" />
          ) : (
            <>
              Assign {coins || 0} coins to the selected user
              {selectedUsers.length > 1 && "s"}
            </>
          )}
        </button>
        {message && (
          <Message variant="success">
            <p style={{color: "#fff"}}>
              Coins have been successfully assigned.
            </p>
          </Message>
        )}
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div
          className={AdminLinksStyles.modalOverlay}
          onClick={() => setIsModalOpen(false)}>
          <div
            className={AdminLinksStyles.modalContent}
            onClick={(e) => e.stopPropagation()}>
            {/* HEADER */}
            <div className={AdminLinksStyles.modalHeader}>
              <h3 className={AdminLinksStyles.modalTitle}>Select Users</h3>

              <button
                className={AdminLinksStyles.modalCloseBtn}
                onClick={() => setIsModalOpen(false)}>
                ✕
              </button>
            </div>

            {/* SEARCH */}
            <input
              className={AdminLinksStyles.modalSearch}
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />

            {/* LIST */}
            <div className={AdminLinksStyles.modalList}>
              {filteredUsers.length === 0 ? (
                <p className={AdminLinksStyles.emptyText}>No users found</p>
              ) : (
                filteredUsers.map((user: User) => (
                  <div
                    key={user.id}
                    className={AdminLinksStyles.userCard}
                    onClick={() => onSelectUser(user)}>
                    <div className={AdminLinksStyles.userName}>
                      {user.firstName} {user.lastName}
                    </div>
                    <div className={AdminLinksStyles.userEmail}>
                      {user.email}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCoinsContainer;
